/**
 * Commerce mode — what this app is FOR, for this tenant (spec §6.1, C5/C6).
 *
 * Everything adaptive in the shell reads from here rather than from the WhatsApp line: a tenant
 * that sells on the web has a complete app with no line at all, and a tenant whose only channel
 * is a WhatsApp line without the AI is an inbox with orders on the side.
 *
 * Pure and React-free on purpose (same reasoning as `lib/session-scope.ts`): the mode rules are
 * the kind of thing that has to be readable in one screen and reviewable without a component.
 */

import { EXTERNAL_AGENT_CHANNEL_AVAILABLE } from '@/types/sell-channel';
import type { MasterClientChannels, ScopedBrand, SessionAccess } from '@/types/auth';

/**
 * 1 = WhatsApp only, no AI: the inbox is the product, the board is secondary.
 * 2 = anything else (AI on, or web on, or the external agent on): the orders board is the product.
 */
export type CommerceMode = 1 | 2;

export function commerceMode(access: SessionAccess | null | undefined): CommerceMode {
  if (!access) return 2;
  const { whatsapp, web, externalAgent } = access.channels;
  // The external agent counts only once it ships (`@/types/sell-channel`): until then it produces no
  // orders, so it must not promote a manual-WhatsApp tenant out of the inbox and onto a board that
  // would always be empty.
  const externalAgentSells = EXTERNAL_AGENT_CHANNEL_AVAILABLE && externalAgent.enabled;
  const manualWhatsappOnly = whatsapp.enabled && !whatsapp.aiAgentEnabled && !web.enabled && !externalAgentSells;
  return manualWhatsappOnly ? 1 : 2;
}

/**
 * Whether a brand has the orders surfaces (board, orders list, discounts). The ORDERS capability is
 * the AI agent's "take orders over WhatsApp" switch, and nothing on the web side reads it: a
 * storefront always takes orders, so an enabled web channel opens those surfaces on its own.
 */
export function takesOrders(
  hasOrdersCapability: boolean,
  channels: Pick<MasterClientChannels, 'web'> | null | undefined,
): boolean {
  return hasOrdersCapability || (channels?.web.enabled ?? false);
}

/**
 * Where `/` lands. Mode 1 lands on the inbox; mode 2 lands on the board — unless the brand cannot
 * take orders at all, in which case there is no board to land on, or the brand has a POS connected
 * and a WhatsApp channel: the POS works the orders, so the board has little left to do and the
 * operator's day is the inbox.
 *
 * This is ONLY the landing: whether the board exists is `takesOrders` alone, never the mode. A
 * mode-1 brand still takes orders by hand from the chat and reaches the board at `/board`.
 */
export function homePath(
  access: SessionAccess | null | undefined,
  ordersCapability: boolean,
  hasPosProvider: boolean,
): '/board' | '/conversations' {
  // No inbox for this session: the board is the only place its day can start.
  if (!showInbox(access)) return ordersCapability ? '/board' : '/conversations';
  if (commerceMode(access) === 1) return '/conversations';
  if (hasPosProvider && showWhatsappChrome(access)) return '/conversations';
  return ordersCapability ? '/board' : '/conversations';
}

/**
 * WhatsApp-specific chrome (the inbox, templates, the playground, the AI kill switch) exists only
 * for a tenant whose WhatsApp channel is on — not for a web-only one.
 */
export function showWhatsappChrome(access: SessionAccess | null | undefined): boolean {
  return access?.channels.whatsapp.enabled ?? false;
}

/**
 * The inbox (and every link into a chat, and the handoff alerts): the WhatsApp chrome, minus a
 * sucursal session whose brand hides its conversations from locations. Display-only.
 */
export function showInbox(access: SessionAccess | null | undefined): boolean {
  return showWhatsappChrome(access) && !access?.conversationsHidden;
}

/**
 * The aggregate counterpart: a parent master client has no `access` block of its own (its
 * channels are its children's), so the inbox exists when ANY brand in the filter sells over
 * WhatsApp — the same rule `aggregateHasOrders` applies to the board. Narrowing the filter to
 * web-only brands is what takes the chats away again.
 */
export function aggregateShowsWhatsappChrome(brands: readonly ScopedBrand[]): boolean {
  return brands.some((brand) => brand.channels.whatsapp.enabled);
}

/**
 * Whether a WhatsApp line is actually connected. The channel can be on with no line yet, and the
 * per-line surfaces (templates, flows) answer 404 `LINE_REQUIRED` in that window.
 */
export function hasLine(access: SessionAccess | null | undefined): boolean {
  return (access?.channels.whatsapp.lines.length ?? 0) > 0;
}
